import {useEffect, useState} from "react";
import classNames from "classnames";
import {useLocation} from "react-router-dom";
import {shareState} from "../../app/share-state";
import {useGoogleAdInterstitial} from "../../hooks/use-google-ad-interstitial";
import {APP_CONST, GOOGLE_ADS_INTERSTITIAL} from "../../consts";
import {localStorageUtils} from "../../infrastructure/utils/locale-storage-utils";
import {sessionStorageUtils} from "../../infrastructure/utils/session-storage-utils";
import styles from "./google-ad-interstitial.module.scss";

export const GoogleAdInterstitial = () => {
  const location = useLocation();
  const [showCloseButton, setShowCloseButton] = useState(false);
  const [shouldInitialize, setShouldInitialize] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const [showMask, setShowMask] = useState(true);

  const adConfig = {
    adUnitPath: GOOGLE_ADS_INTERSTITIAL.adUnitPath,
    targeting: {
      "CountryId": String(shareState.settings?.countryId || ""),
      "Bolao_Skin": shareState.theme
    },
    divId: GOOGLE_ADS_INTERSTITIAL.divId,
    size: [320, 480],
    shouldInitialize: shouldInitialize
  };

  const {adContainerId, hasError, adFilled} = useGoogleAdInterstitial(adConfig);

  useEffect(() => {
    const canShowAd = checkAllConditions();

    if (canShowAd) {
      sessionStorageUtils.setItem(
        APP_CONST.SESSION_STORAGE_KEYS.interstitialAdSessionShown,
        'true'
      );

      if (shareState.isIOS) {
        window.OpenInterstitialAdInterstitial = (visible) => {
          setShouldInitialize(visible === 'true');
          setIsVisible(visible === 'true');
        };
        shareState.nativeFunctionsUtils.callFunction("openIntestitialNow");
        updateDailyCount();
      } else {
        setShouldInitialize(canShowAd);
        if(canShowAd) {
          setTimeout(() => {
            setIsVisible(false);
          }, 10000);
        }
      }

    }
  }, []);

  useEffect(() => {
    const isOnWizardPage = location.pathname.includes(APP_CONST.ROUTE.wizard);
    if (isOnWizardPage) {
      setIsVisible(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (adFilled && shouldInitialize) {
      updateDailyCount();

      // Drop the mask after 600ms once the ad's micro-blink frame has passed
      const maskTimer = setTimeout(() => {
        setShowMask(false);
      }, 600);

      const closeBtnTimer = setTimeout(() => {
        setShowCloseButton(true);
      }, 2000);

      return () => {
        clearTimeout(maskTimer);
        clearTimeout(closeBtnTimer);
      };
    }
  }, [adFilled, shouldInitialize]);

  const checkAllConditions = () => {
    const isOnWizardPage: Boolean = location.pathname.includes(APP_CONST.ROUTE.wizard);
    const isMobile: Boolean = shareState.isMobileView;
    const isUserLoggedIn: Boolean = !!shareState.user?.userId;
    const isWizardVisible: Boolean = !shareState.wizard.isVisible;
    const isInterstitialEnabled: Boolean = !!shareState.settings?.showInterstitial;
    const isAdsAllowedInWebView: Boolean = !shareState.isWebView || !!shareState.nativeGameConfig?.showAds;
    const isIsrael: Boolean = shareState.settings?.countryId === 6;
    const sessionShown: String = sessionStorageUtils.getItem(APP_CONST.SESSION_STORAGE_KEYS.interstitialAdSessionShown);
    const isNotShownInSession: Boolean = sessionShown !== 'true';
    const maxPerDay: Number = shareState.settings?.maxInterstitialPerDay;
    const today: String = getTodayDate();
    const lastDate: String = localStorageUtils.getItem(APP_CONST.LOCAL_STORAGE_KEYS.interstitialAdLastDate);

    const dailyCount: Number = parseInt(
      localStorageUtils.getItem(APP_CONST.LOCAL_STORAGE_KEYS.interstitialAdDailyCount) || '0',
      10
    );

    if (lastDate !== today) {
      localStorageUtils.setItem(APP_CONST.LOCAL_STORAGE_KEYS.interstitialAdDailyCount, '0');
      localStorageUtils.setItem(APP_CONST.LOCAL_STORAGE_KEYS.interstitialAdLastDate, today);
    }

    const hasRemainingDailySlots: Boolean = lastDate !== today || dailyCount < maxPerDay;

    const canShow: Boolean = !isOnWizardPage && isMobile && isUserLoggedIn &&
      isWizardVisible && isInterstitialEnabled && isAdsAllowedInWebView &&
      isNotShownInSession && isIsrael && hasRemainingDailySlots;

    return canShow;
  };

  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  const updateDailyCount = () => {
    const today = getTodayDate();
    const dailyCount = parseInt(
      localStorageUtils.getItem(APP_CONST.LOCAL_STORAGE_KEYS.interstitialAdDailyCount) || '0', 10);

    localStorageUtils.setItem(APP_CONST.LOCAL_STORAGE_KEYS.interstitialAdDailyCount, String(dailyCount + 1));
    localStorageUtils.setItem(APP_CONST.LOCAL_STORAGE_KEYS.interstitialAdLastDate, today);
  };

  if (hasError || !isVisible) {
    return null;
  }

  const showOverlay: Boolean = shouldInitialize && adFilled;

  return (
    <div className={classNames(styles.overlay)} style={{display: showOverlay ? 'flex' : 'none'}}>
      <div className={styles.ad_wrapper}>
        <div id={adContainerId} className={classNames(styles.ad_container)}/>
        <div className={classNames(styles.ad_mask, {[styles.hidden]: !showMask})}/>
      </div>
      <div className={classNames(styles.close_button_wrapper, {[styles.show_close_button]: showCloseButton})}
           onClick={() => setIsVisible(false)}>
        <button className={classNames(styles.close_button)}>X</button>
      </div>
    </div>
  );
};